import {
  BufferGeometry,
  Camera,
  Color,
  ColorRepresentation,
  Group,
  Material,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { Text } from 'troika-three-text';

import { GcpModel } from '../../../../../typings/api/skymap/rest/v0/.common';
import { roundToDecimals } from '../../../utils/math/number';
import { CoordinateManager } from '../../coordinate-manager';
import { renderOrders } from '../../render-orders';
import { disposeRecursive } from '../../utilities/three/dispose';
import { ViewerEnvironmentInstance } from '../../viewer-environment';
import { ElementObj } from '../element-obj';
import { TextBatch } from './text-batch';

const TMP_V2 = new Vector2();
const TMP_A = new Vector3();
const TMP_B = new Vector3();
const TMP_C = new Vector3();
const TMP_D = new Vector3();
const TARGET_PX = 14;

/**
 * The height in pixels of a label that keeps its size on screen, in its user data. Labels without
 * it are sized in the world.
 */
export const SCREEN_HEIGHT_PX = 'screenHeightPx';

const textMaterial = new MeshBasicMaterial({
  transparent: true,
  depthTest: false,
  depthWrite: false,
  toneMapped: false,
});

/**
 * @param position - The position of the text in world coordinates.
 */
function getWorldScreenH(camera: Camera, position: Vector3, TMP_B: Vector3) {
  if ((camera as PerspectiveCamera).isPerspectiveCamera) {
    const cam = camera as PerspectiveCamera;
    const vFov = (cam.fov * Math.PI) / 180;

    cam.getWorldPosition(TMP_B);
    const dist = position.distanceTo(TMP_B);

    return 2 * dist * Math.tan(vFov / 2);
  } else {
    const cam = camera as OrthographicCamera;
    return (cam.top - cam.bottom) / (cam.zoom || 1);
  }
}

function resetLabelGroup(asset: ElementObj) {
  if (!asset.labelsGroup) {
    return;
  }
  disposeRecursive(asset.labelsGroup);
  asset.remove(asset.labelsGroup);
  asset.labelsGroup = new Group();
  asset.add(asset.labelsGroup);
}

/**
 * Gets the batch that the labels of the parent are drawn in, see TextBatch. There can be
 * thousands, e.g. the height labels of the points of a drawing.
 */
function getLabelBatch(parent: Object3D): TextBatch {
  const existing = parent.children.find((x): x is TextBatch => x instanceof TextBatch);

  if (existing) {
    return existing;
  }

  const batch = new TextBatch();
  batch.material = textMaterial;
  batch.renderOrder = renderOrders.aligner.gcpLabels;
  // The labels are scaled before each render, after the bounds of the batch are computed.
  batch.frustumCulled = false;
  parent.add(batch);

  return batch;
}

/**
 * Creates a label at the position in the coordinates of the parent. It is added to the label batch
 * of the parent with the update, see getLabelBatch.
 */
function initLabel(localPosition: Vector3, color: ColorRepresentation) {
  const text = new Text();
  text.fontSize = 5;
  text.color = color;
  text.fontWeight = 'bold';
  text.position.copy(localPosition);
  // Set the scale to 0 so that the text is not visible before its first render.
  text.scale.setScalar(0);

  return text;
}

/**
 * @param fontSizeMultiplier - Default value is adjusted based on what looks good in the viewer.
 * @param sizeAttenuation - If true, automatically scales labels based on camera distance. Default is true.
 */
function createLabel(
  parent: Object3D,
  localPosition: Vector3,
  color: ColorRepresentation = new Color(1, 1, 1),
  fontSizeMultiplier: number = 1.4,
  sizeAttenuation: boolean = true,
  decimals: number = 2,
) {
  const text = initLabel(localPosition, color);

  text.anchorX = -2.5 / (fontSizeMultiplier * 10);
  text.anchorY = 2 / (fontSizeMultiplier * 10);
  text.text = roundToDecimals(
    parent.localToWorld(localPosition.clone()).z + CoordinateManager.getInstance().getMAS(),
    decimals,
  ).toString();

  const update = (renderer: WebGLRenderer, camera: Camera) => {
    const worldPosition = parent.localToWorld(TMP_A.copy(localPosition));
    const next = roundToDecimals(
      worldPosition.z + CoordinateManager.getInstance().getMAS(),
      decimals,
    ).toString();

    if (text.text !== next) {
      text.text = next;
    }

    // Billboard. (Text faces camera)
    text.quaternion.copy((camera.parent ?? camera).quaternion);

    // Size Attenuation
    if (sizeAttenuation) {
      renderer.getSize(TMP_V2);
      const viewportH = TMP_V2.y;
      const worldScreenH = getWorldScreenH(camera, worldPosition, TMP_B);
      const worldPerPixel = worldScreenH / viewportH;
      const scale = (TARGET_PX * fontSizeMultiplier * worldPerPixel) / text.fontSize;
      text.scale.setScalar(scale);
    } else {
      text.scale.setScalar(fontSizeMultiplier);
    }
  };

  if (sizeAttenuation) {
    text.userData[SCREEN_HEIGHT_PX] = TARGET_PX * fontSizeMultiplier;
  }
  getLabelBatch(parent).addText(text, update);
  void text.sync();

  return text;
}

/**
 * @param fontSizeMultiplier - Default value is adjusted based on what looks good in the viewer.
 * @param sizeAttenuation - If true, automatically scales labels based on camera distance. Default is true.
 */
function createLengthLabel(
  parent: Object3D,
  vertexA: Vector3,
  vertexB: Vector3,
  color: ColorRepresentation = new Color(1, 1, 1),
  fontSizeMultiplier: number = 1.4,
  sizeAttenuation: boolean = true,
  decimals: number = 3,
) {
  const localPosition = vertexA.clone().add(vertexB).divideScalar(2);
  const text = initLabel(localPosition, color);

  text.anchorX = 'center';
  text.anchorY = 'bottom';
  text.text = `${roundToDecimals(vertexA.distanceTo(vertexB), decimals).toString()} ${ViewerEnvironmentInstance.getUnitSymbols().length}`;

  // Store local vertices for rotation calculation
  const localVertexA = vertexA.clone();
  const localVertexB = vertexB.clone();

  const update = (renderer: WebGLRenderer, camera: Camera) => {
    // 1) World positions of segment endpoints
    parent.localToWorld(TMP_A.copy(localVertexA));
    parent.localToWorld(TMP_B.copy(localVertexB));

    // 2) Project to NDC
    TMP_C.copy(TMP_A).project(camera);
    TMP_D.copy(TMP_B).project(camera);

    // 3) Compute 2D direction in *pixel space* to get the real on-screen angle
    let rotationAngle = 0;

    renderer.getSize(TMP_V2);
    const viewportW = TMP_V2.x;
    const viewportH = TMP_V2.y;

    // delta in pixels (proportional is enough; we just need correct aspect)
    TMP_V2.set((TMP_D.x - TMP_C.x) * viewportW, (TMP_D.y - TMP_C.y) * viewportH);

    if (TMP_V2.lengthSq() > 1e-6) {
      rotationAngle = Math.atan2(TMP_V2.y, TMP_V2.x);
    }

    // Optional: prevent upside-down labels (keep within ±90°)
    if (rotationAngle > Math.PI / 2) {
      rotationAngle -= Math.PI;
    } else if (rotationAngle < -Math.PI / 2) {
      rotationAngle += Math.PI;
    }

    // 4) Billboard – face the camera
    text.quaternion.copy((camera.parent ?? camera).quaternion);

    // 5) Rotate around local Z (camera forward) to align with segment
    if (Math.abs(rotationAngle) > 0.001) {
      text.rotateZ(rotationAngle);
      // If it looks mirrored, flip sign:
      // text.rotateZ(-rotationAngle);
    }

    // 6) Size Attenuation
    if (sizeAttenuation) {
      const worldPosition = parent.localToWorld(TMP_A.copy(localPosition));
      const worldScreenH = getWorldScreenH(camera, worldPosition, TMP_B);
      const worldPerPixel = worldScreenH / viewportH;
      const scale = (TARGET_PX * fontSizeMultiplier * worldPerPixel) / text.fontSize;
      text.scale.setScalar(scale);
    } else {
      text.scale.setScalar(fontSizeMultiplier);
    }
  };

  if (sizeAttenuation) {
    text.userData[SCREEN_HEIGHT_PX] = TARGET_PX * fontSizeMultiplier;
  }
  getLabelBatch(parent).addText(text, update);
  void text.sync();

  return text;
}

function createGcpLabel(gcp: GcpModel, origo: Vector3, textMaterial: Material) {
  const pos = new Vector3(...gcp.enu).sub(origo);

  const text = new Text();

  text.text = gcp.name;
  text.fontSize = 5;
  text.color = 0xffffff;
  text.anchorX = -2.5;
  text.anchorY = 'bottom-baseline';
  text.fontWeight = 'bold';
  text.position.copy(pos);
  text.renderOrder = renderOrders.aligner.gcpLabels;
  text.material = textMaterial;

  text.outlineColor = 0x000000;
  text.outlineOpacity = 0.8;
  text.outlineWidth = 1.5;

  // Store the original method bound to the text instance
  const troikaOnBeforeRender = text.onBeforeRender.bind(text);

  const TMP_V2 = new Vector2();
  const TMP_A = new Vector3();
  const TMP_B = new Vector3();
  const TARGET_PX = 14;

  text.onBeforeRender = function (
    this: Text,
    renderer: WebGLRenderer,
    scene: Scene,
    camera: Camera,
    geometry: BufferGeometry,
    material: Material,
    group: Group,
  ) {
    troikaOnBeforeRender.call(this, renderer, scene, camera, geometry, material, group);

    // Billboard. (Text faces camera)
    this.quaternion.copy((camera.parent ?? camera).quaternion);

    // Size Attenuation
    renderer.getSize(TMP_V2);
    const viewportH = TMP_V2.y;
    const worldScreenH = getWorldScreenH(camera, text.getWorldPosition(TMP_A), TMP_B);
    const worldPerPixel = worldScreenH / viewportH;
    const scale = (TARGET_PX * worldPerPixel) / text.fontSize;
    text.scale.setScalar(scale);
  };

  void text.sync();

  return text;
}

export const heightLabelUtils = {
  createLabel,
  createLengthLabel,
  createGcpLabel,
  resetLabelGroup,
};
